import { useEffect, useState, type FormEvent } from 'react';
import { AdminLayout } from '../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Badge,
  Pagination,
  Alert,
  Modal,
} from '../components/ui';
import {
  getAdminAkunList,
  resetAkunPassword,
  unlockAkun,
  deactivateAkun,
  reactivateAkun,
} from '../features/admin-akun/api';
import type { AdminAkunListItem } from '@app/shared';
import { RoleCode } from '@app/shared';

const roleLabels: Record<string, string> = {
  [RoleCode.ADMIN]: 'Admin',
  [RoleCode.GURU]: 'Guru',
  [RoleCode.KEPALA_SEKOLAH]: 'Kepala Sekolah',
};

const statusBadgeColor: Record<string, 'green' | 'red' | 'gray'> = {
  aktif: 'green',
  nonaktif: 'red',
  terkunci: 'gray',
};

const statusLabels: Record<string, string> = {
  aktif: 'Aktif',
  nonaktif: 'Nonaktif',
  terkunci: 'Terkunci',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type ModalAction = {
  type: 'reset' | 'unlock' | 'deactivate' | 'reactivate';
  account: AdminAkunListItem;
} | null;

export function AdminAkunPage() {
  const [rows, setRows] = useState<AdminAkunListItem[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState<ModalAction>(null);
  const [password, setPassword] = useState('');
  const [alasan, setAlasan] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | undefined> = { page, limit: meta.limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status_akun = statusFilter;
      if (roleFilter) params.role = roleFilter;
      const result = await getAdminAkunList(params);
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat daftar akun');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
  }, []);

  const handleSearch = () => {
    void load(1);
  };

  const handlePageChange = (page: number) => {
    void load(page);
  };

  const openModal = (action: ModalAction) => {
    setModal(action);
    setPassword('');
    setAlasan('');
    setError(null);
    setMessage(null);
  };

  const closeModal = () => {
    setModal(null);
    setPassword('');
    setAlasan('');
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!modal) return;

    // Validate based on modal type
    if ((modal.type === 'reset' || modal.type === 'unlock') && (!password || password.length < 8)) {
      setError('Password minimal 8 karakter');
      return;
    }
    if (modal.type === 'deactivate' && (!alasan || alasan.length < 3)) {
      setError('Alasan minimal 3 karakter');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (modal.type === 'reset') {
        await resetAkunPassword(modal.account.id, { password });
        setMessage('Password berhasil direset');
      } else if (modal.type === 'unlock') {
        await unlockAkun(modal.account.id, { password });
        setMessage('Akun berhasil dibuka');
      } else if (modal.type === 'deactivate') {
        await deactivateAkun(modal.account.id, { alasan });
        setMessage('Akun berhasil dinonaktifkan');
      } else if (modal.type === 'reactivate') {
        await reactivateAkun(modal.account.id);
        setMessage('Akun berhasil diaktifkan kembali');
      }
      closeModal();
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memproses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Manajemen Akun">
      {message && (
        <Alert type="success" key={message}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert type="error" key={error}>
          {error}
        </Alert>
      )}

      <Card>
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ flex: '1 1 200px' }}>
            <Input
              placeholder="Cari nama, username, atau NIP/NIK..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </div>
          <div style={{ width: '160px' }}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Semua Status</option>
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
              <option value="terkunci">Terkunci</option>
            </Select>
          </div>
          <div style={{ width: '160px' }}>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Semua Role</option>
              <option value={RoleCode.ADMIN}>Admin</option>
              <option value={RoleCode.GURU}>Guru</option>
              <option value={RoleCode.KEPALA_SEKOLAH}>Kepala Sekolah</option>
            </Select>
          </div>
          <Button onClick={handleSearch}>Cari</Button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem' }}>Memuat...</p>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Nama</Th>
                  <Th>NIP/NIK</Th>
                  <Th>Username</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Terakhir Login</Th>
                  <Th>Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <Td colSpan={8} style={{ textAlign: 'center' }}>
                      Tidak ada data akun
                    </Td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <Td>{row.nama}</Td>
                      <Td>{row.nip_nik}</Td>
                      <Td>{row.username}</Td>
                      <Td>{row.email ?? '—'}</Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {row.roles.map((r) => (
                            <Badge key={r} color="blue">
                              {roleLabels[r] ?? r}
                            </Badge>
                          ))}
                        </div>
                      </Td>
                      <Td>
                        <Badge color={statusBadgeColor[row.status_akun] ?? 'gray'}>
                          {statusLabels[row.status_akun] ?? row.status_akun}
                        </Badge>
                      </Td>
                      <Td>{formatDateTime(row.terakhir_login_at)}</Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <Button
                            variant="secondary"
                            onClick={() => openModal({ type: 'reset', account: row })}
                          >
                            Reset Password
                          </Button>
                          {row.status_akun === 'terkunci' && (
                            <Button
                              variant="secondary"
                              onClick={() => openModal({ type: 'unlock', account: row })}
                            >
                              Buka Kunci
                            </Button>
                          )}
                          {row.status_akun === 'aktif' && (
                            <Button
                              variant="danger"
                              onClick={() => openModal({ type: 'deactivate', account: row })}
                            >
                              Nonaktifkan
                            </Button>
                          )}
                          {row.status_akun === 'nonaktif' && (
                            <Button
                              variant="secondary"
                              onClick={() => openModal({ type: 'reactivate', account: row })}
                            >
                              Aktifkan
                            </Button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            {meta.total_pages > 1 && (
              <Pagination
                page={meta.page}
                totalPages={meta.total_pages}
                onChange={handlePageChange}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={modal !== null}
        onClose={closeModal}
        title={
          modal?.type === 'reset'
            ? 'Reset Password'
            : modal?.type === 'unlock'
              ? 'Buka Kunci Akun'
              : modal?.type === 'deactivate'
                ? 'Nonaktifkan Akun'
                : 'Aktifkan Kembali Akun'
        }
      >
        {modal && (
          <form onSubmit={handleSubmit}>
            <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
              {modal.type === 'reset'
                ? `Reset password untuk akun ${modal.account.username} (${modal.account.nama})`
                : modal.type === 'unlock'
                  ? `Buka kunci akun ${modal.account.username} (${modal.account.nama})`
                  : modal.type === 'deactivate'
                    ? `Nonaktifkan akun ${modal.account.username} (${modal.account.nama}). Akun yang dinonaktifkan tidak dapat login.`
                    : `Aktifkan kembali akun ${modal.account.username} (${modal.account.nama}).`}
            </p>
            {error && <Alert type="error">{error}</Alert>}
            {(modal.type === 'reset' || modal.type === 'unlock') && (
              <Input
                label="Password Baru"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimal 8 karakter"
                autoFocus
              />
            )}
            {modal.type === 'deactivate' && (
              <Input
                label="Alasan Penonaktifan"
                value={alasan}
                onChange={(e) => setAlasan(e.target.value)}
                placeholder="Minimal 3 karakter"
                autoFocus
              />
            )}
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'flex-end',
                marginTop: '1rem',
              }}
            >
              <Button variant="secondary" onClick={closeModal} type="button">
                Batal
              </Button>
              <Button
                type="submit"
                disabled={saving}
                variant={modal.type === 'deactivate' ? 'danger' : undefined}
              >
                {saving
                  ? 'Menyimpan...'
                  : modal.type === 'deactivate'
                    ? 'Nonaktifkan'
                    : modal.type === 'reactivate'
                      ? 'Aktifkan'
                      : 'Simpan'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </AdminLayout>
  );
}
